/**
 * Dynamic Data/View helper methods for LoaderPreProcess (private methods only)
 * The public ApplyDynamicViewHandlers method is in LoaderPreProcess.ts
 * Applies dynamic data handlers from Global/dynamicdata.json if configured
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { JsonArray, JsonObject, JsonValue, JsonValueType } from '../../app/json/jsonValue.js';
import { NavigationDecision } from '../../model/index.js';
import { RegistryStore } from '../../registry/registryStore.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { DynamicConfig } from '../../registry/dynamicConfig.js';
import type { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import { AppSiteResult } from '../../registry/iface/appSiteResult.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * Scans HTML for {{Name}} placeholder markers introduced by a DynamicView handler.
 * Skips framework markers ({{#, {{@, {{$, {{/, {{^, {{!) and non-alphanumeric names.
 * Returns the matched names in order of first appearance, mirroring csharp ScanForLatePlaceholders.
 */
function scanForLatePlaceholders(html: string): string[] {
    const found: string[] = [];
    if (!html) return found;

    let scanPos = 0;
    while (scanPos < html.length) {
        const openStart = html.indexOf('{{', scanPos);
        if (openStart < 0) break;

        if (openStart + 2 < html.length) {
            const nextChar = html[openStart + 2];
            if (nextChar === '#' || nextChar === '@' || nextChar === '$' || nextChar === '/' || nextChar === '^' || nextChar === '!') {
                scanPos = openStart + 2;
                continue;
            }
        }

        const closeStart = html.indexOf('}}', openStart + 2);
        if (closeStart < 0) break;

        const name = html.substring(openStart + 2, closeStart).trim();
        if (!name || !CommonUtil.isAlphaNumeric(name)) {
            scanPos = openStart + 2;
            continue;
        }

        found.push(name);
        scanPos = closeStart + 2;
    }
    return found;
}

/**
 * Finds a key in a Map case-insensitively
 */
function findKeyInMapCaseInsensitive(map: DynamicConfigMap, key: string): string | null {
    if (!map || !key) return null;
    const lowerKey = key.toLowerCase();
    for (const k of map.keys()) {
        if (k.toLowerCase() === lowerKey) {
            return k;
        }
    }
    return null;
}

function executeWithReadOnly<T>(
    paramJson: JsonObject,
    componentJson: JsonObject,
    configJson: JsonObject | null,
    action: () => T
): T {
    const paramWasReadOnly = paramJson.isReadOnly;
    const componentWasReadOnly = componentJson.isReadOnly;
    const configWasReadOnly = configJson ? configJson.isReadOnly : false;

    paramJson.isReadOnly = true;
    componentJson.isReadOnly = true;
    if (configJson) {
        configJson.isReadOnly = true;
    }

    try {
        return action();
    } finally {
        paramJson.isReadOnly = paramWasReadOnly;
        componentJson.isReadOnly = componentWasReadOnly;
        if (configJson) {
            configJson.isReadOnly = configWasReadOnly;
        }
    }
}


/**
 * Dynamic data methods to be attached to LoaderPreProcess prototype
 */
export const dynamicMethods = {
    // Mirrors the resolution applyDynamicDataHandlers uses (the AppSite's dynamicdata.json, else the first
    // SearchAppSite that has one), so the inheritance fast-path never skips a handler bound in a search AppSite.
    _hasDynamicDataHandlers(this: LoaderPreProcess, appSite: string, componentName: string): boolean {
        const { dynamicConfig } = this.resolveDynamicDataConfigSource(appSite);
        if (!dynamicConfig) return false;
        return this.getDynamicDataMatchKey(componentName, dynamicConfig) !== null;
    },

    // Mirrors the resolution applyDynamicActionHandlers uses (search-appsite aware).
    _hasDynamicActionHandlers(this: LoaderPreProcess, appSite: string, componentName: string): boolean {
        const { dynamicConfig } = this.resolveDynamicActionConfigSource(appSite);
        if (!dynamicConfig) return false;
        return this.getDynamicDataMatchKey(componentName, dynamicConfig) !== null;
    },

    applyViewToTemplate(this: LoaderPreProcess, appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView: string | null, flowStack: string[] | null, configJson: JsonObject | null, result: import('./iLoaderPreProcess.js').GetTemplateResult, clientMode: boolean = false) {
        void clientMode;
        const applied = this.applyDynamicViewHandlers(appSite, appView || '', currentTemplateName, componentName, flowStack?.length ?? 0, result.template.originalContent, result.json, result.resolvedAppSite, result.resolvedTemplateName, configJson, timing);
        return { ...result, viewHtml: applied.html, latePlaceholders: applied.newPlaceholders };
    },

    // #region DynamicData Helpers (Private)

    /**
     * Builds an immutable parent context map from the flow stack.
     * Walks forward through flowStack, collecting all parent key-value pairs.
     * Later parents overwrite earlier ones; resolved values overwrite static cache values.
     */
    _buildParentContext(this: LoaderPreProcess, appSite: string, flowStack: string[]): Map<string, string> | null {
        if (!flowStack || flowStack.length === 0) {
            return null;
        }

        const context = new Map<string, string>();
        const appSiteLower = appSite.toLowerCase();

        for (let i = 0; i < flowStack.length; i++) {
            const parentName = flowStack[i];
            const parentKey = `${appSiteLower}_${parentName.toLowerCase()}`;

            const cached = this._store.tryGetTemplates(appSite);
            if (cached) {
                let parentTemplate: { template: { originalContent: string }; json: JsonObject | null } | null = null;
                for (const [cacheName, cacheValue] of cached.entries()) {
                    const parts = cacheName.split('_');
                    if (parts.length >= 2) {
                        const templateName = parts.slice(1).join('_');
                        if (templateName.toLowerCase() === parentName.toLowerCase()) {
                            parentTemplate = cacheValue;
                            break;
                        }
                    }
                }

                if (parentTemplate && parentTemplate.json) {
                    for (const [jsonKey, jsonValue] of parentTemplate.json.entries()) {
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            context.set(jsonKey.toLowerCase(), strValue);
                        }
                    }
                }
            }

            const resolvedJson = this._dynamicResults.resolvedTemplateJson.get(parentKey).asObject();
            if (resolvedJson) {
                for (const [jsonKey, jsonValue] of resolvedJson.entries()) {
                    let strValue: string | null = null;
                    if (typeof jsonValue === 'string') {
                        strValue = jsonValue;
                    } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                        strValue = jsonValue.asString();
                    }
                    if (strValue !== null) {
                        context.set(jsonKey.toLowerCase(), strValue);
                    }
                }
            }
        }

        return context.size > 0 ? context : null;
    },

    executeWithReadOnly<T>(this: LoaderPreProcess, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T): T {
        return executeWithReadOnly(paramJson, componentJson, configJson, action);
    },

    shouldSkipDynamicViewHandlerInvocation(this: LoaderPreProcess, componentHtml: string, componentName: string, resolvedComponentName: string): boolean {
        return !componentHtml ||
            componentName.startsWith('!') ||
            resolvedComponentName === this._config!.defaultSkipTemplateName ||
            resolvedComponentName === this._config!.defaultReplaceTemplateName;
    },

    resolveDynamicViewConfigSource(this: LoaderPreProcess, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicViewConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicViewConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    getDynamicViewKeysToProcess(this: LoaderPreProcess, appSite: string, componentName: string, dynamicConfig: DynamicConfigMap, dynamicConfigAppSite: string): { keysToProcess: string[]; dynamicConfig: DynamicConfigMap; dynamicConfigAppSite: string } {
        const keysToProcess: string[] = [];
        if (dynamicConfig.has('*')) {
            keysToProcess.push('*');
        }

        let foundSpecificMatch = false;
        const matchedKey = findKeyInMapCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey && matchedKey !== '*') {
            keysToProcess.push(matchedKey);
            foundSpecificMatch = true;
        }

        if (!foundSpecificMatch) {
            for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
                const searchConfig = this.getDynamicViewConfig(searchAppSite);
                if (!searchConfig) {
                    continue;
                }

                const searchMatchedKey = findKeyInMapCaseInsensitive(searchConfig, componentName);
                if (searchMatchedKey && searchMatchedKey !== '*') {
                    keysToProcess.push(searchMatchedKey);
                    dynamicConfig = searchConfig;
                    dynamicConfigAppSite = searchAppSite;
                    foundSpecificMatch = true;
                    break;
                }

                if (!keysToProcess.includes('*') && searchConfig.has('*')) {
                    keysToProcess.unshift('*');
                    dynamicConfig = searchConfig;
                    dynamicConfigAppSite = searchAppSite;
                    break;
                }
            }
        }

        return { keysToProcess, dynamicConfig, dynamicConfigAppSite };
    },

    shouldSkipViewHandler(this: LoaderPreProcess, iViewType: string): boolean {
        if (!RegistryStore.containsView(iViewType)) {
            return true;
        }

        if (!this._skipDynamicView || this._skipDynamicView.trim() === '') {
            return false;
        }

        const normalizedPatterns = this._skipDynamicView.trim();
        if (normalizedPatterns === '*') {
            return true;
        }

        const lowerViewType = iViewType.toLowerCase();
        for (const pattern of normalizedPatterns.split(',')) {
            const trimmedPattern = pattern.trim().toLowerCase();
            if (!trimmedPattern) {
                continue;
            }

            if (trimmedPattern === '*' || trimmedPattern === lowerViewType) {
                return true;
            }
        }

        return false;
    },

    tryResolveDynamicViewProvider(this: LoaderPreProcess, iViewType: string, dynamicConfigAppSite: string, paramFile: string): { viewProvider: ReturnType<typeof RegistryStore.getDynamicView> | null; paramJson: JsonObject } {
        const params = this.getDynamicViewParams(dynamicConfigAppSite, paramFile);
        const paramJson = new JsonObject();
        if (params) {
            for (const [key, value] of params) {
                if (value.isString) {
                    paramJson.set(key, value.asString());
                }
            }
        }

        if (!RegistryStore.containsView(iViewType)) {
            return { viewProvider: null, paramJson };
        }

        return { viewProvider: RegistryStore.getDynamicView(iViewType), paramJson };
    },

    executeDynamicViewHandler(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, viewProvider: NonNullable<ReturnType<typeof RegistryStore.getDynamicView>>, timing: PhaseTiming): string {
        return timing.measure(TimingPhase.Dynamic, () => {
        const resultHtml = this.executeWithReadOnly(
            paramJson,
            componentJson,
            configJson,
            () => RegistryStore.invokeDynamicView(viewProvider.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, componentHtml, paramJson, componentJson, configJson)
        );
        return resultHtml ?? componentHtml;
        });
    },

    recordDynamicViewResult(this: LoaderPreProcess, componentName: string, iViewType: string, resultHtml: string): void {
        this._dynamicResults.dynamicViewResults.set(`${componentName}_${iViewType}`, resultHtml);
    },

    resolveDynamicDataConfigSource(this: LoaderPreProcess, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicDataConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    getDynamicDataMatchKey(this: LoaderPreProcess, componentName: string, dynamicConfig: DynamicConfigMap): string | null {
        const matchedKey = findKeyInMapCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey !== null) {
            return matchedKey;
        }

        if (dynamicConfig.has('*')) {
            return '*';
        }

        return null;
    },

    shouldSkipDataHandler(this: LoaderPreProcess, iDataType: string): boolean {
        if (!RegistryStore.containsData(iDataType)) {
            return true;
        }

        if (!this._skipDynamicData || this._skipDynamicData.trim() === '') {
            return false;
        }

        const normalizedPatterns = this._skipDynamicData.trim();
        if (normalizedPatterns === '*') {
            return true;
        }

        const lowerDataType = iDataType.toLowerCase();
        for (const pattern of normalizedPatterns.split(',')) {
            const trimmedPattern = pattern.trim().toLowerCase();
            if (!trimmedPattern) {
                continue;
            }

            if (trimmedPattern === '*' || trimmedPattern === lowerDataType) {
                return true;
            }
        }

        return false;
    },

    tryResolveDynamicDataProvider(this: LoaderPreProcess, iDataType: string, dynamicConfigAppSite: string, paramFile: string): { dataProvider: ReturnType<typeof RegistryStore.getDynamicData> | null; paramJson: JsonObject } {
        const params = this.getDynamicDataParams(dynamicConfigAppSite, paramFile);
        const paramJson = new JsonObject();
        if (params) {
            for (const [key, value] of params) {
                paramJson.set(key, value);
            }
        }

        if (!RegistryStore.containsData(iDataType)) {
            return { dataProvider: null, paramJson };
        }

        return { dataProvider: RegistryStore.getDynamicData(iDataType), paramJson };
    },

    executeDynamicDataHandler(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, dataHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicData>>, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming): JsonObject {
        return timing.measure(TimingPhase.Dynamic, () => {
        const resultJson = this.executeWithReadOnly(paramJson, componentJson, configJson,
            () => RegistryStore.invokeDynamicData(dataHandler.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
        return resultJson;
        });
    },

    mergeDynamicDataResult(this: LoaderPreProcess, componentJson: JsonObject, resultJson: JsonObject | null): void {
        if (resultJson === null) {
            return;
        }

        // Redaction runs BEFORE the mode branch, not inside the merge arm. Replace mode substitutes the
        // handler's whole return for the component data, so redacting only on the merge path would make
        // ReplaceHandlerReturn a way to publish the sensitive values the merge path masks.
        const safeResultJson = JsonUtility.redactSensitive(resultJson);
        if (this._config!.replaceHandlerReturn) {
            this.replaceJsonObjectContents(componentJson, safeResultJson);
            return;
        }

        for (const [k, v] of safeResultJson) {
            componentJson.set(k, v);
        }
    },

    replaceJsonObjectContents(this: LoaderPreProcess, targetJson: JsonObject, sourceJson: JsonObject): void {
        targetJson.clear();
        for (const [key, value] of sourceJson) {
            targetJson.set(key, value);
        }
    },

    recordDynamicDataResult(this: LoaderPreProcess, componentName: string, iDataType: string, componentJson: JsonObject): void {
        // componentJson is read-only during the IData handler call. The loader merges
        // the handler's resultJson into componentJson between iterations, so multiple
        // entries for one component alias the latest merged state ("alias-to-final").
        // Tests pass against this aliasing because consumers read the final aggregate state.
        this._dynamicResults.dynamicDataResults.set(`${componentName}_${iDataType}`, componentJson);
        this.syncDynamicActionResultsAfterDataMutation(componentName, componentJson);
    },

    syncDynamicActionResultsAfterDataMutation(this: LoaderPreProcess, componentName: string, afterJson: unknown): void {
        if (!this._actionMode || afterJson === null || afterJson === undefined) {
            return;
        }

        const prefix = `${componentName}_`.toLowerCase();
        for (const key of Array.from(this._dynamicResults.dynamicActionResults.keys())) {
            if (key.toLowerCase().startsWith(prefix)) {
                this._dynamicResults.dynamicActionResults.set(key, JsonValue.object(JsonUtility.redactSensitive(afterJson as JsonObject)));
            }
        }
    },

    resolveDynamicActionConfigSource(this: LoaderPreProcess, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicActionConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicActionConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    tryResolveDynamicActionProvider(this: LoaderPreProcess, iActionType: string, dynamicConfigAppSite: string, paramFile: string): { registered: boolean; paramJson: JsonObject } {
        const paramJson = this.getDynamicActionParams(dynamicConfigAppSite, paramFile) ?? new JsonObject();
        if (!RegistryStore.containsAction(iActionType)) {
            return { registered: false, paramJson };
        }

        return { registered: true, paramJson };
    },

    tryBuildParentJsonValueHandlerForAction(this: LoaderPreProcess, appSite: string, flowStack: string[] | null, contextJson: JsonObject | null = null): ((key: string) => string | null) | null {
        const parentContext = flowStack && flowStack.length > 0 ? this._buildParentContext(appSite, flowStack) : null;
        if (!parentContext && !contextJson) {
            return null;
        }

        return (key: string): string | null => {
            const parentValue = parentContext?.get(key.toLowerCase());
            if (parentValue !== undefined && parentValue !== '') {
                return parentValue;
            }
            if (contextJson?.has(key)) {
                // F-SEC1: surface non-string scalars (notably the IsAuthenticated bool) as their string form.
                // asString() returns null for a bool/number, which previously hid IsAuthenticated from the
                // in-handler authz check and forced it to authorize on UserRole alone.
                const ctxRaw = contextJson.get(key);
                let ctxValue = ctxRaw.asString();
                if ((ctxValue === null || ctxValue === '') && !ctxRaw.isNull) {
                    ctxValue = ctxRaw.toString();
                }
                if (ctxValue !== null && ctxValue !== '') {
                    return ctxValue;
                }
            }
            return parentValue ?? null;
        };
    },

    executeDynamicAction(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionTypeName: string, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming): void {
        timing.measure(TimingPhase.Dynamic, () => {
        this.executeWithReadOnly(
            paramJson,
            componentJson,
            actionJson,
            () => RegistryStore.invokeDynamicAction(actionTypeName, appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), actionJson, getParentJsonValue)
        );
        });
    },

    executeDynamicActionHandler(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionTypeName: string, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming): void {
        timing.measure(TimingPhase.Dynamic, () => {
        this.executeDynamicAction(appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, actionJson, actionTypeName, getParentJsonValue, timing);
        });
    },

    recordDynamicActionResult(this: LoaderPreProcess, componentName: string, iActionType: string, componentJson: JsonObject): void {
        // componentJson is read-only during DoAction (executeWithReadOnly) and current
        // IAction handlers communicate via transientStore, never mutating componentJson.
        // syncDynamicActionResultsAfterDataMutation overwrites this entry after any
        // later data merge, so storing the reference here is safe.
        this._dynamicResults.dynamicActionResults.set(`${componentName}_${iActionType}`, JsonValue.object(JsonUtility.redactSensitive(componentJson)));
    },

    buildAppSiteSearchOrder(this: LoaderPreProcess, appSite: string): string[] {
        const appSitesToSearch: string[] = [appSite];
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !appSitesToSearch.includes(trimmed)) {
                    appSitesToSearch.push(trimmed);
                }
            }
        }

        return appSitesToSearch;
    },

    // #endregion

    // #region DynamicView Apply (Private)

    /**
     * Applies dynamic view handlers from Global/dynamicview.json to wrap/modify component HTML
     */
    applyDynamicViewHandlers(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming): { html: string; newPlaceholders: string[] } {
        return timing.measure(TimingPhase.Dynamic, () => {
        if (this.shouldSkipDynamicViewHandlerInvocation(componentHtml, componentName, resolvedComponentName)) {
            return { html: componentHtml, newPlaceholders: [] };
        }
        return this._applyDynamicViewHandlers(appSite, appView, appFile, componentName, componentDepth, componentHtml, componentJson, resolvedAppSite, resolvedComponentName, configJson, timing);
        });
    },

    _applyDynamicViewHandlers(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming): { html: string; newPlaceholders: string[] } {
        let { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicViewConfigSource(appSite);
        if (!dynamicConfig) {
            return { html: componentHtml, newPlaceholders: [] };
        }

        const dynamicViewKeys = this.getDynamicViewKeysToProcess(appSite, componentName, dynamicConfig, dynamicConfigAppSite);
        const { keysToProcess } = dynamicViewKeys;
        dynamicConfig = dynamicViewKeys.dynamicConfig;
        dynamicConfigAppSite = dynamicViewKeys.dynamicConfigAppSite;

        if (keysToProcess.length === 0) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicViewHandlers: No handlers for componentName='${componentName}', available keys: ${Array.from(dynamicConfig.keys()).join(', ')}`, 'LoaderPreProcess');
            }
            return { html: componentHtml, newPlaceholders: [] };
        }

        let resultHtml = componentHtml;

        // Process handlers from each matched key
        for (const matchedKey of keysToProcess) {
            const handlers = dynamicConfig.get(matchedKey);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicViewHandlers: Found ${handlers.length} handlers for ${componentName} (key: ${matchedKey})`, 'LoaderPreProcess');
            }

            for (const handlerObj of handlers) {
                const iViewType = handlerObj.iType;
                if (!iViewType) {
                    continue;
                }

                if (this.shouldSkipViewHandler(iViewType)) {
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`ApplyDynamicViewHandlers: Skipping handler '${iViewType}' due to SkipDynamicView='${this._skipDynamicView}'`, 'LoaderPreProcess');
                    }
                    continue;
                }

                const paramFile = handlerObj.iParamFile || '';
                const { viewProvider, paramJson } = this.tryResolveDynamicViewProvider(iViewType, dynamicConfigAppSite, paramFile);
                if (!viewProvider) {
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`ApplyDynamicViewHandlers: IView handler '${iViewType}' not registered`, 'LoaderPreProcess');
                    }
                    continue;
                }
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicViewHandlers: Calling ${iViewType} for ${componentName}`, 'LoaderPreProcess');
                }

                let compJson: JsonObject;
                const compJsonValue: unknown = componentJson;
                if (compJsonValue instanceof JsonObject) {
                    compJson = compJsonValue;
                } else if (compJsonValue instanceof Map) {
                    compJson = new JsonObject();
                    for (const [key, value] of compJsonValue) {
                        compJson.set(key, value as string);
                    }
                } else if (compJsonValue && typeof compJsonValue === 'object' && !(compJsonValue instanceof JsonArray)) {
                    compJson = JsonObject.fromPlainObject(compJsonValue as PlainJsonObject);
                } else {
                    compJson = new JsonObject();
                }

                try {
                    resultHtml = this.executeDynamicViewHandler(appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, resultHtml, paramJson, compJson, configJson, viewProvider, timing);

                    if (resultHtml.indexOf('\r') !== -1) {
                        resultHtml = CommonUtil.normalizeFileContent(resultHtml);
                    }

                    this.recordDynamicViewResult(componentName, iViewType, resultHtml);
                } catch (ex) {
                    Logger.error(`ApplyDynamicViewHandlers: Error calling ${iViewType}: ${(ex as Error).message}`, 'LoaderPreProcess');
                    Logger.fallback("F-ENG3", `IView handler threw, component left un-enriched: handler=${iViewType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                    throw ex;
                }
            }
        }

        // Post-handler metadata pass: scan the modified HTML for {{Name}} component references
        // introduced by the handler output. Skip framework markers ({{#, {{@, {{$, {{/, {{^, {{!).
        // The engine consumes this list to resolve late-bound placeholders without re-scanning the
        // same HTML — the discovery cost is paid here exactly once per handler invocation.
        const newPlaceholders = scanForLatePlaceholders(resultHtml);
        return { html: resultHtml, newPlaceholders };
    },

    // #endregion

    // #region DynamicData Apply (Private)

    /**
     * Applies dynamic data handlers to component JSON.
     */
    applyDynamicDataHandlers(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, timing: PhaseTiming, configJson: JsonObject | null = null, flowStack: string[] | null = null): void {
        timing.measure(TimingPhase.Dynamic, () => {
        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicDataConfigSource(appSite);
        if (!dynamicConfig) {
            return;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        Logger.debug(`ApplyDynamicDataHandlers: Found ${handlers.length} handlers for ${componentName}`, 'LoaderPreProcess');

        for (const handlerObj of handlers) {
            const iDataType = handlerObj.iType;
            if (!iDataType) {
                continue;
            }

            if (this.shouldSkipDataHandler(iDataType)) {
                Logger.debug(`ApplyDynamicDataHandlers: Skipping handler '${iDataType}' due to SkipDynamicData='${this._skipDynamicData}'`, 'LoaderPreProcess');
                continue;
            }

            const paramFile = handlerObj.iParamFile || '';

            const { dataProvider, paramJson } = this.tryResolveDynamicDataProvider(iDataType, dynamicConfigAppSite, paramFile);
            if (!dataProvider) {
                Logger.debug(`ApplyDynamicDataHandlers: IData handler '${iDataType}' not registered`, 'LoaderPreProcess');
                continue;
            }

            Logger.debug(`ApplyDynamicDataHandlers: Calling ${iDataType} for ${componentName}`, 'LoaderPreProcess');

            try {
                // Data handlers resolve parent values the SAME way action handlers do: a non-empty inherited
                // parent value wins, otherwise fall back to the server-verified request context. This surfaces
                // auth keys (UserId/IsAuthenticated/UserRole) to context-aware data providers (e.g. listing the
                // signed-in user's own API keys) — the parent-only resolver could not reach the request context.
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);
                const resultJson = this.executeDynamicDataHandler(appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, configJson, dataProvider, getParentJsonValue, timing);

                this.mergeDynamicDataResult(componentJson, resultJson);
                this.recordDynamicDataResult(componentName, iDataType, componentJson);
            } catch (ex) {
                Logger.error(`ApplyDynamicDataHandlers: Error calling ${iDataType}: ${(ex as Error).message}`, 'LoaderPreProcess');
                Logger.fallback("F-ENG3", `IData handler threw, component left un-enriched: handler=${iDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
        });
    },

    // #endregion

    // #region DynamicClientData Apply (Private)

    resolveDynamicClientDataConfigSource(this: LoaderPreProcess, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicClientDataConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    /**
     * Resolves the params for a PURE client handler — one the AppSite declares with DataSource ⊇ "Pure" in
     * dynamicclientdata.json, meaning it produces its output from its declared params alone and never
     * consumes a server-produced raw island.
     *
     * A pure binding names the IClient processor in its IType (e.g. TimeClient), whereas an Embedded binding
     * names the IClientData preparer. That is what lets one config file carry both shapes without ambiguity,
     * and it is why this matches on the handler name off the element rather than on the island key.
     *
     * Returns null when the AppSite declares no pure binding for this component/handler, which is the signal
     * to skip the handler exactly as an absent island always has.
     */
    tryResolvePureClientHandlerParams(this: LoaderPreProcess, appSite: string, componentName: string, clientHandlerName: string): JsonObject | null {
        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (dynamicConfig === null) {
            return null;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (matchedKey === null) {
            return null;
        }

        for (const handler of dynamicConfig.get(matchedKey) ?? []) {
            if (!handler.hasDataSource('Pure')) continue;
            if (handler.iType.toLowerCase() !== clientHandlerName.toLowerCase()) continue;

            return this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
        }

        return null;
    },

    // True when a CLIENT-DATA preparer (dynamicclientdata.json) is bound to this component — mirrors the
    // resolution applyDynamicClientDataHandlers uses (search-appsite aware). The inheritance fast-path must
    // honor this so the Embedded island is produced even when the component carries no server
    // Data/Action/Global handler.
    _hasDynamicClientDataHandlers(this: LoaderPreProcess, appSite: string, componentName: string): boolean {
        const { dynamicConfig } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) return false;
        return this.getDynamicDataMatchKey(componentName, dynamicConfig) !== null;
    },

    /**
     * Applies CLIENT-DATA preparer handlers from dynamicclientdata.json for a component. Runs the registered
     * IClientData producer (context-light, via RegistryStore.invokeDynamicClientData) and records the RAW set
     * into dynamicClientDataResults — it never mutates componentJson (the raw is for the browser to filter,
     * not the server-baked DataJson). The merge records ONLY when Embedded is declared (page-1 raw ships with
     * the render); a Remote-only binding is served on demand by getClientDataSlice (the /api/handler-data
     * endpoint, P3A), never produced at render. configJson is passed as the producer's callerParams (carries
     * paging keys for a partial/paged slice).
     */
    applyDynamicClientDataHandlers(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming, flowStack: string[] | null = null): void {
        timing.measure(TimingPhase.Dynamic, () => {
        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (!matchedKey) {
            return;
        }

        const handlers: DynamicConfig[] | null = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const handler of handlers) {
            const iClientDataType = handler.iType;
            if (!iClientDataType) {
                continue;
            }

            // The merge produces the raw only for an Embedded (or Embedded,Remote hybrid) binding — page-1
            // raw ships with the render. A Remote-only binding is served on demand by getClientDataSlice
            // (the /api/handler-data endpoint), never produced at render, so skip it here.
            if (!handler.hasDataSource('Embedded')) {
                continue;
            }

            if (!RegistryStore.containsClientData(iClientDataType)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicClientDataHandlers: IClientData handler '${iClientDataType}' not registered`, 'LoaderPreProcess');
                }
                continue;
            }

            const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();

            try {
                // F2: pin paramJson + componentJson + configJson read-only across the producer — ClientData
                // records the RAW set only and must never mutate the resolved component JSON (invariant I4).
                const rawJson = this.executeWithReadOnly(paramJson, componentJson, configJson,
                    () => RegistryStore.invokeDynamicClientData(iClientDataType, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
                this._dynamicResults.dynamicClientDataResults.set(`${componentName}_${iClientDataType}`, JsonValue.object(rawJson));
            } catch (ex) {
                Logger.error(`ApplyDynamicClientDataHandlers: Error calling ${iClientDataType}: ${(ex as Error).message}`, 'LoaderPreProcess');
                // F-ENG3 (handler exception): an IClientData producer threw, so we log it and surface the exception —
                // the component ships without its client-data island and the failure is invisible to the response.
                Logger.fallback('F-ENG3', `IClientData handler threw, island not produced: handler=${iClientDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
        });
    },

    /**
     * CLIENT tier consumption (§2B.5) — PreProcess variant, mirror of the Normal engine. Fills resolvedJson from
     * the delivered island via the registered IClient handler (substitutes for the server data handler that
     * ConfigClient does not ship) and re-emits the island at the page tail — so the client re-merge matches the
     * server render byte-for-byte. Extracts EVERY data-client-handler declaration from the rendered componentHtml;
     * per declaration reads the raw from the delivered map (_config.dynamicClientData) else the HTML island keyed
     * <ComponentName>_<ClientDataKey>, runs the registered IClient over resolvedJson (F2 read-only pin on
     * paramJson+rawJson), merges the filtered result into resolvedJson, records the RAW into
     * dynamicClientDataResults (re-emit) and the FILTERED into dynamicClientResults. Runs only under clientMode.
     */
    applyClientHandlers(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string | null, resolvedJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming, flowStack: string[] | null = null): void {
        timing.measure(TimingPhase.Dynamic, () => {
            this.preserveDeliveredClientData(componentName);
            if (!componentHtml) {
                return;
            }

            const declarations = CommonUtil.extractClientHandlerDeclarations(componentHtml);
            if (declarations.length === 0) {
                return;
            }

            // Island raw source: the ConfigClient console round-trip carries the delivered map
            // (_config.dynamicClientData); a real browser has no such map and reads the island from the rendered DOM.
            const delivered = this._config!.dynamicClientData;

            const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

            for (const decl of declarations) {
                if (!RegistryStore.containsClientHandler(decl.handlerName)) {
                    continue;
                }

                let rawJson: JsonObject;
                let pureParamJson: JsonObject | null = null;
                // The island this declaration CONSUMES. Empty on the pure lane, which consumes none.
                let islandKey = '';
                // The key the consumed OUTPUT is recorded under. Always set, so a pure declaration is still
                // cross-checked by the round-trip: named for the preparer on the island lane (symmetric with
                // dynamicClientDataResults) and for the handler itself on the pure lane, which has no preparer.
                let resultKey = componentName + '_' + decl.handlerName;
                if (!decl.clientDataKey) {
                    // PURE lane, DECLARED by the absence of data-client-data: this handler derives its output from
                    // its params alone, so no island is looked up at all. The AppSite's dynamicclientdata binding
                    // must still declare DataSource "Pure" for it to run, which keeps the JSON declaration the
                    // security bound — a component cannot opt itself into island-free execution by editing its HTML.
                    pureParamJson = this.tryResolvePureClientHandlerParams(appSite, componentName, decl.handlerName);
                    if (pureParamJson === null) {
                        continue;
                    }
                    rawJson = new JsonObject();
                } else {
                    islandKey = componentName + '_' + decl.clientDataKey;
                    resultKey = islandKey;
                    if (delivered !== null && delivered.has(islandKey) && delivered.get(islandKey).isObject) {
                        rawJson = delivered.get(islandKey).asObject()!;
                    } else {
                        const rawStr = CommonUtil.extractClientDataIsland(componentHtml, islandKey);
                        // A DATA-DEPENDENT declaration named a preparer, so a missing island is a MISSING PRODUCER
                        // and the handler is skipped. ⛔ It is deliberately NOT retried on the pure path: the two
                        // lanes are disjoint by declaration now, so a broken producer can no longer be masked by a
                        // pure binding that happens to share the handler name.
                        if (!rawStr) {
                            continue;
                        }
                        rawJson = JsonUtility.parseJsonString(rawStr);
                        if (rawJson.size === 0) {
                            continue;
                        }
                    }
                }

                try {
                    // A pure handler reads its params from the BINDING (where FixedTime/DateFormat live alongside
                    // the server tier's own copy), overlaid with the element's data-client-handler-param so a
                    // per-instance override still wins. A data-dependent handler keeps reading the element only.
                    let paramJson: JsonObject;
                    if (pureParamJson === null) {
                        paramJson = JsonUtility.parseJsonString(decl.param) ?? new JsonObject();
                    } else {
                        paramJson = pureParamJson;
                        const declOverride = JsonUtility.parseJsonString(decl.param);
                        if (declOverride !== null) {
                            for (const [k, v] of declOverride.entries()) {
                                paramJson.set(k, v);
                            }
                        }
                    }
                    // F2: pin the delivered island raw + paramJson read-only across the IClient invocation.
                    const filtered = this.executeWithReadOnly(paramJson, rawJson, null,
                        () => RegistryStore.invokeDynamicClient(decl.handlerName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, resolvedJson, this.getTransientStore(), configJson, getParentJsonValue, rawJson) ?? new JsonObject());
                    for (const [k, v] of filtered.entries()) {
                        resolvedJson.set(k, v);
                    }
                    // Re-emit the delivered island at the page tail, matching the server byte-for-byte. A PURE
                    // handler consumed no island — rawJson is the empty stand-in built above — so there is nothing
                    // to re-emit; emitting it would append an island the server never produced.
                    if (pureParamJson === null) {
                        this._dynamicResults.dynamicClientDataResults.set(islandKey, JsonValue.object(rawJson));
                    }
                    // Record the CONSUMED (filtered) output — the client mirror of the server DynamicData.
                    // Keyed by resultKey, NOT islandKey: a pure declaration has no island key, and recording it
                    // under an empty key would collide every pure handler in the component onto one slot.
                    this._dynamicResults.dynamicClientResults.set(resultKey, JsonValue.object(filtered));
                } catch (ex) {
                    Logger.error(`ApplyClientHandlers: Error running ${decl.handlerName}: ${(ex as Error).message}`, 'LoaderPreProcess');
                    // F-ENG3 (handler exception): an IClient consumer threw, so we log it and surface the exception — the
                    // component keeps its un-enriched JSON and the failure is invisible to the response.
                    Logger.fallback('F-ENG3', `IClient handler threw, component left un-enriched: handler=${decl.handlerName} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                    throw ex;
                }
            }
        });
    },

    preserveDeliveredClientData(this: LoaderPreProcess, componentName: string): void {
        for (const [islandKey, island] of CommonUtil.selectDeliveredClientDataIslands(this._config?.dynamicClientData ?? null, componentName)) {
            this._dynamicResults.dynamicClientDataResults.set(islandKey, island);
        }
    },

    /**
     * P3A ON-DEMAND slice for the /api/handler-data endpoint. Resolves the client-data binding for handlerName
     * and returns the raw producer output via the SAME context-light producer the merge uses
     * (RegistryStore.invokeDynamicClientData) — one producer, two seams. Does NOT run a page merge; callerParams
     * is handed straight in as paging keys. Returns null when the AppSite declares no binding for the handler
     * (endpoint → 400, no arbitrary handler probing).
     *
     * Not gated on DataSource ⊇ Remote: that flag says WHERE the island is delivered at render time (Embedded =
     * with the server's own render), not who is allowed to ask for it. A client rendering the AppSite itself
     * needs exactly the data the Embedded island would have carried, so gating here would force every Embedded
     * AppSite to redeclare itself merely to be client-mergeable. The declaration in dynamicclientdata.json
     * remains the bound: an undeclared handler is still unreachable.
     */
    getClientDataSlice(this: LoaderPreProcess, appSite: string, handlerName: string, callerParams: JsonObject): JsonObject | null {
        if (!handlerName) {
            return null;
        }

        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return null;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || handler.iType.toLowerCase() !== handlerName.toLowerCase()) {
                    continue;
                }
                if (!RegistryStore.containsClientData(handlerName)) {
                    return null;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Remote/on-demand endpoint: no merge context, so the merge-only params are passed empty and the
                // per-request config rides in as configJson (the producer reads only appSite/component/paramJson).
                return RegistryStore.invokeDynamicClientData(handlerName, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null) ?? new JsonObject();
            }
        }

        return null;
    },

    /**
     * Produces every declared client-data island for the AppSite, keyed "<component>_<IType>" — the island set a
     * client merge of this AppSite needs when the page carries none of its own (see ILoader.getClientDataIslands).
     * Runs the same context-light producer as the merge and the single-slice endpoint, over every binding
     * dynamicclientdata.json declares, so the set is byte-identical to the islands a server render of this
     * AppSite would have embedded.
     */
    getClientDataIslands(this: LoaderPreProcess, appSite: string, callerParams: JsonObject): JsonObject {
        const islands = new JsonObject();

        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return islands;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || !RegistryStore.containsClientData(handler.iType)) {
                    continue;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Same shape as the merge's applyDynamicClientDataHandlers record: key by the BOUND component,
                // because that is the component the consumer's island lookup derives (component + "_" + key).
                const raw = RegistryStore.invokeDynamicClientData(handler.iType, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null);
                if (raw) {
                    islands.set(boundComponentName + '_' + handler.iType, JsonValue.object(raw));
                }
            }
        }

        return islands;
    },

    /**
     * True when the AppSite declares a client-data binding with a registered producer (see ILoader.hasClientData).
     * Same predicate as getClientDataIslands, declaration lookup only.
     */
    hasClientData(this: LoaderPreProcess, appSite: string): boolean {
        const { dynamicConfig } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return false;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (handler.iType && RegistryStore.containsClientData(handler.iType)) {
                    return true;
                }
            }
        }

        return false;
    },

    // #endregion

    // #region DynamicAction Apply (Private)

    applyDynamicActionHandlers(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentJson: JsonObject, resolvedAppSite: string, resolvedComponentName: string, timing: PhaseTiming, configJson: JsonObject | null = null, contextJson: JsonObject | null = null, flowStack: string[] | null = null): void {
        timing.measure(TimingPhase.Dynamic, () => {
        if (!this._actionMode) {
            return;
        }

        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicActionConfigSource(appSite);
        if (!dynamicConfig) {
            return;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`ApplyDynamicActionHandlers: Found ${handlers.length} handlers for ${componentName}`, 'LoaderPreProcess');
        }

        const actionJson = configJson;

        for (const handlerObj of handlers) {
            const iActionType = handlerObj.iType;
            if (!iActionType) {
                continue;
            }

            const paramFile = handlerObj.iParamFile || '';

            const { registered, paramJson } = this.tryResolveDynamicActionProvider(iActionType, dynamicConfigAppSite, paramFile);
            if (!registered) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicActionHandlers: IAction handler '${iActionType}' not registered`, 'LoaderPreProcess');
                }
                continue;
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicActionHandlers: Calling ${iActionType} for ${componentName}`, 'LoaderPreProcess');
            }

            try {
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, contextJson ?? this._requestContextJson);
                this.executeDynamicActionHandler(appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, actionJson, iActionType, getParentJsonValue, timing);

                this.recordDynamicActionResult(componentName, iActionType, componentJson);
            } catch (ex) {
                Logger.error(`ApplyDynamicActionHandlers: Error calling ${iActionType}: ${(ex as Error).message}`, 'LoaderPreProcess');
                throw ex;
            }
        }
        });
    },

    // #endregion

    // #region DynamicAppSite Apply (Public)

    /**
     * Resolves the AppSite/AppView to render by running the IAppSite handlers configured for the
     * requested AppSite (dynamicappsite.json, keyed by AppSite name). Mirrors applyDynamicDataHandlers
     * ownership. AppSite-specific handlers run first, then the wildcard ("*") handlers LAST. Dormant
     * infrastructure until IAppSite handlers are registered and the merge pipeline calls it (later phase).
     */
    applyDynamicAppSiteHandlers(this: LoaderPreProcess, appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming): AppSiteResult {
        return timing.measure(TimingPhase.Dynamic, () => {
        this._requestContextJson = contextJson;
        // Reset the per-merge navigation trace; one decision is appended per handler invocation below.
        this._lastNavigationTrace = [];

        let curAppSite = appSite;
        let curAppView = appView ?? '';

        const appSiteConfig = this.getDynamicAppSiteConfig(appSite);
        if (!appSiteConfig) return new AppSiteResult(curAppSite, curAppView);

        const specificHandlers = appSiteConfig.get(appSite);
        const wildcardHandlers = appSite === '*' ? null : appSiteConfig.get('*');
        const handlers: DynamicConfig[] = [];
        if (specificHandlers && Array.isArray(specificHandlers)) handlers.push(...specificHandlers);
        if (wildcardHandlers && Array.isArray(wildcardHandlers)) handlers.push(...wildcardHandlers);
        if (handlers.length === 0) return new AppSiteResult(curAppSite, curAppView);

        for (const handler of handlers) {
            const iAppSiteType = handler.iType;
            if (!iAppSiteType) {
                continue;
            }

            const provider = RegistryStore.getDynamicAppSite(iAppSiteType);
            if (!provider) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicAppSiteHandlers: IAppSite handler '${iAppSiteType}' not registered`, 'LoaderPreProcess');
                }
                continue;
            }

            const paramJson = this.getDynamicAppSiteParams(appSite, handler.iParamFile || '') ?? new JsonObject();
            try {
                const resolved = RegistryStore.invokeDynamicAppSite(provider.typeName, curAppSite, curAppView, paramJson, contextJson, configJson);
                if (resolved) {
                    const changed = curAppSite !== resolved.appSite || curAppView !== resolved.appView;
                    if (changed) {
                        Logger.flow(`ApplyDynamicAppSiteHandlers: redefined ${curAppSite}/${curAppView} -> ${resolved.appSite}/${resolved.appView} via ${iAppSiteType}`, 'PreProcess.MergeLifecycle');
                    }
                    // Preserve every hop (change or no-op) so a scenario can assert the path, not only the endpoint.
                    this._lastNavigationTrace.push(new NavigationDecision(
                        iAppSiteType, curAppSite, curAppView, resolved.appSite, resolved.appView, changed, resolved.reason));
                    curAppSite = resolved.appSite;
                    curAppView = resolved.appView;
                }
            } catch (ex) {
                // F-SEC5 (hardened, fail-closed): an AppSite handler computes the authoritative auth redirect
                // (e.g. unauth Admin -> Login). If it throws, keeping curAppSite/curAppView at the requested
                // (protected) values would render the PROTECTED page — a fail-OPEN. Surface the failure instead.
                Logger.error(`ApplyDynamicAppSiteHandlers: Error calling ${iAppSiteType}: ${(ex as Error).message}`, 'LoaderPreProcess');
                Logger.fallback("F-SEC5", `AppSite handler ${iAppSiteType} threw; failing closed (would have kept protected appSite=${curAppSite}/${curAppView}): ${(ex as Error).message}`);
                throw ex;
            }
        }

        if (curAppSite !== appSite) {
            this.ensureAppSiteLoaded(curAppSite, timing);
        }

        return new AppSiteResult(curAppSite, curAppView);
        });
    }

    // #endregion
};
